import { useCallback, useRef, useState } from "react";
import api, { API } from "@/lib/api";

// Voice input via MediaRecorder + Whisper; voice output via browser SpeechSynthesis.
export function useVoice() {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const resolveRef = useRef(null);
  const audioRef = useRef(null);

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setTranscribing(true);
      try {
        const form = new FormData();
        form.append("audio", blob, "speech.webm");
        const { data } = await api.post("/voice/transcribe", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        resolveRef.current && resolveRef.current(data.text || "");
      } catch {
        resolveRef.current && resolveRef.current("");
      } finally {
        setTranscribing(false);
      }
    };
    mediaRef.current = mr;
    mr.start();
    setRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      if (mediaRef.current && mediaRef.current.state !== "inactive") {
        mediaRef.current.stop();
      }
      setRecording(false);
    });
  }, []);

  const speak = useCallback(async (text, onEnd, voiceOverride) => {
    const clean = (text || "").replace(/\[\[ASSESSMENT_COMPLETE\]\]/g, "").trim();
    if (!clean) { onEnd && onEnd(); return; }
    // stop any current playback
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    try {
      const token = localStorage.getItem("archon_token");
      const voice = voiceOverride || localStorage.getItem("archon_voice") || "jarvis";
      const res = await fetch(`${API}/voice/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text: clean, voice }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      const finish = () => { URL.revokeObjectURL(url); if (audioRef.current === audio) audioRef.current = null; onEnd && onEnd(); };
      audio.onended = finish;
      audio.onerror = finish;
      await audio.play();
    } catch {
      onEnd && onEnd();
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  return { recording, transcribing, startRecording, stopRecording, speak, stopSpeaking };
}
